import { forwardRef, type ButtonHTMLAttributes, type ForwardedRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/cn';

// Tone/size action button (ported from web; brand tokens abstracted:
// the legacy brand shadow → shadow-xs, the legacy hover token → --primary-hover).
const actionButtonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md border font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-60 [&_[data-icon]]:size-3.5 [&_svg]:shrink-0',
  {
    variants: {
      tone: {
        default: 'border-border bg-card text-foreground hover:bg-muted',
        primary:
          'border-primary bg-primary text-primary-foreground hover:border-primary-hover hover:bg-primary-hover',
        destructive: 'bg-destructive border-destructive text-destructive-foreground',
        ghost:
          'border-transparent bg-transparent text-muted-foreground shadow-none hover:bg-muted hover:text-foreground',
      },
      size: {
        sm: 'h-8 gap-1.5 px-2.5 text-[12px]',
        md: 'h-9 gap-2 px-3 text-[12px]',
        lg: 'h-10 gap-2 px-3.5 text-[13px]',
        'icon-sm': 'size-8 p-0 text-[12px]',
        'icon-md': 'size-9 p-0 text-[12px]',
      },
    },
    defaultVariants: { tone: 'default', size: 'md' },
  },
);

export interface ActionButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof actionButtonVariants> {}

export const ActionButton = forwardRef(function ActionButton(
  { className, tone, size, type = 'button', ...props }: ActionButtonProps,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(actionButtonVariants({ tone, size }), className)}
      {...props}
    />
  );
});
